import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Headphones, CheckCircle2, RotateCcw, MessageCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const REASON_LABELS = {
  COMPLAINT_OR_NEGOTIATION: { label: 'Complaint / Negotiation', color: 'bg-red-100 text-red-700' },
  'AI_ESCALATION:complaint': { label: 'AI escalation · Complaint', color: 'bg-red-100 text-red-700' },
  'AI_ESCALATION:refund': { label: 'AI escalation · Refund', color: 'bg-orange-100 text-orange-700' },
  'AI_ESCALATION:return': { label: 'AI escalation · Return', color: 'bg-orange-100 text-orange-700' },
  'AI_ESCALATION:video_call': { label: 'AI escalation · Video call', color: 'bg-purple-100 text-purple-700' },
  'AI_ESCALATION:other': { label: 'AI escalation · Talk to human', color: 'bg-blue-100 text-blue-700' },
};

function reasonChip(reason) {
  const meta = REASON_LABELS[reason] || { label: reason || 'Needs human', color: 'bg-fab-elevated text-fab-text-secondary' };
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${meta.color}`}>{meta.label}</span>;
}

export default function Support() {
  const nav = useNavigate();
  const [tab, setTab] = useState('open'); // 'open' | 'resolved' | 'all'
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  const load = (t = tab) => {
    setLoading(true);
    api.get(`/support/queue?status_filter=${t}`)
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);

  const resolve = async (cid) => {
    setActing(cid);
    try {
      await api.post(`/support/conversations/${cid}/resolve`);
      toast.success('Marked as resolved');
      load(tab);
    } catch (e) {
      toast.error('Could not resolve');
    } finally {
      setActing(null);
    }
  };
  const reopen = async (cid) => {
    setActing(cid);
    try {
      await api.post(`/support/conversations/${cid}/reopen`);
      toast.success('Reopened');
      load(tab);
    } finally {
      setActing(null);
    }
  };

  const openInbox = (conv) => {
    nav('/dashboard/whatsapp', { state: { focusConversationId: conv.id } });
  };

  const counts = {
    open: items.filter(i => i.support_status === 'open').length,
    resolved: items.filter(i => i.support_status === 'resolved').length,
  };

  return (
    <div className="space-y-5 fade-up" data-testid="support-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-fab-primary-lighter flex items-center justify-center">
            <Headphones className="w-5 h-5 text-fab-primary" />
          </div>
          <div>
            <div className="font-display font-bold text-fab-text text-[16px]">Customer Support Queue</div>
            <div className="text-[12px] text-fab-text-muted">Conversations the AI escalated for your personal attention.</div>
          </div>
        </div>
        <div className="ml-auto inline-flex p-1 rounded-lg bg-fab-elevated">
          {['open', 'resolved', 'all'].map((t) => (
            <button
              key={t}
              data-testid={`support-tab-${t}`}
              onClick={() => setTab(t)}
              className={`px-3 py-1.5 rounded-md text-[12px] font-semibold capitalize ${tab === t ? 'bg-white shadow-sm text-fab-text' : 'text-fab-text-secondary'}`}
            >
              {t}{tab === t && t === 'open' && counts.open > 0 ? ` · ${counts.open}` : ''}
            </button>
          ))}
        </div>
      </div>

      <div className="fab-card divide-y divide-fab-primary-lighter">
        {loading ? (
          <div className="px-5 py-10 text-center text-fab-text-muted text-[13px] flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading...
          </div>
        ) : items.length === 0 ? (
          <div className="px-5 py-10 text-center text-fab-text-muted text-[13px]">
            {tab === 'open' ? 'Nothing here — your customers are happy! 🎉' : 'No items in this view.'}
          </div>
        ) : items.map((c) => {
          const status = c.support_status;
          const isOpen = status === 'open';
          return (
            <div key={c.id} className="px-5 py-4 hover:bg-fab-elevated" data-testid={`support-row-${c.id}`}>
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isOpen ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-700'}`}>
                  {isOpen ? <AlertTriangle className="w-5 h-5" /> : <CheckCircle2 className="w-5 h-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold text-fab-text text-[14px]">
                      {c.customer_name || c.customer_phone || 'Unknown customer'}
                    </div>
                    <span className="font-mono text-[11px] text-fab-text-muted">{c.customer_phone}</span>
                    {reasonChip(c.support_reason)}
                  </div>
                  {c.support_last_excerpt && (
                    <div className="text-[12px] text-fab-text-secondary mt-1 line-clamp-2">
                      &ldquo;{c.support_last_excerpt}&rdquo;
                    </div>
                  )}
                  <div className="text-[11px] text-fab-text-muted mt-1">
                    {isOpen
                      ? `Opened ${c.support_opened_at ? new Date(c.support_opened_at).toLocaleString() : ''}`
                      : `Resolved ${c.support_resolved_at ? new Date(c.support_resolved_at).toLocaleString() : ''}${c.support_resolved_via ? ` · via ${c.support_resolved_via}` : ''}`
                    }
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    data-testid={`support-open-chat-${c.id}`}
                    onClick={() => openInbox(c)}
                    className="px-3 py-1.5 rounded-lg border border-fab-primary-lighter text-[12px] font-semibold text-fab-text-secondary hover:bg-fab-primary-lighter hover:text-fab-primary flex items-center gap-1.5"
                    title="Open conversation in WhatsApp inbox"
                  >
                    <MessageCircle className="w-4 h-4" /> Open chat
                  </button>
                  {isOpen ? (
                    <button
                      data-testid={`support-resolve-${c.id}`}
                      disabled={acting === c.id}
                      onClick={() => resolve(c.id)}
                      className="btn-primary px-3 py-1.5 rounded-lg text-[12px] font-semibold flex items-center gap-1.5 disabled:opacity-50"
                    >
                      {acting === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                      Mark resolved
                    </button>
                  ) : (
                    <button
                      data-testid={`support-reopen-${c.id}`}
                      disabled={acting === c.id}
                      onClick={() => reopen(c.id)}
                      className="px-3 py-1.5 rounded-lg border border-fab-primary-lighter text-[12px] font-semibold text-fab-text-secondary hover:bg-fab-elevated flex items-center gap-1.5 disabled:opacity-50"
                    >
                      {acting === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                      Reopen
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
